import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, fmtDay, Member, SUBJECT_PATH, Task } from '../api';
import { useSession } from '../session';
import { ErrorBox, SourceBadge, useAsync } from './ui';

let membersCache: { entity: string | null; p: Promise<Member[]> } | null = null;
/** A saját cég munkatársai (felelős választásához), cégenként egyszer lekérve. */
export function useMembers() {
  const s = useSession();
  const [m, setM] = useState<Member[]>([]);
  useEffect(() => {
    if (!membersCache || membersCache.entity !== s.entityId) {
      membersCache = { entity: s.entityId, p: api<{ items: Member[] }>('/members').then((r) => r.items).catch(() => []) };
    }
    membersCache.p.then(setM);
  }, [s.entityId]);
  return m;
}

const PRIO: Record<string, string> = { high: 'sürgős', normal: '', low: 'ráér' };

/** Egy feladat sora: pipálással lezárható; a tárgy (cég/kontakt/deal) linkje opcionális. */
export function TaskRow({ t, onChange, showSubject = true, showAssignee = false }: { t: Task; onChange: () => void; showSubject?: boolean; showAssignee?: boolean }) {
  const a = useAsync();
  const toggle = async () => {
    if (await a.run(() => api(`/tasks/${t.id}`, { method: 'PATCH', body: { status: t.status === 'done' ? 'open' : 'done' } }))) onChange();
  };
  return (
    <li className={`task ${t.status === 'done' ? 'done' : ''}`}>
      <label className="check">
        <input type="checkbox" checked={t.status === 'done'} onChange={toggle} disabled={a.busy} aria-label="Kész" />
        <span className="task-title">{t.title}</span>
      </label>
      <span className="task-meta">
        {t.priority !== 'normal' && <span className={`badge ${t.priority === 'high' ? 'badge-risk-external' : ''}`}>{PRIO[t.priority]}</span>}
        <SourceBadge src={t.field_sources?.title} />
        {t.due_date && <span className={t.overdue ? 'overdue' : 'muted'}>{fmtDay(t.due_date)}{t.due_time ? ` ${t.due_time}` : ''}</span>}
        {showAssignee && t.assignee_name && <span className="muted">{t.assignee_name}</span>}
        {showSubject && t.subject_type && t.subject_id && <Link to={`${SUBJECT_PATH[t.subject_type]}/${t.subject_id}`}>{t.subject_label ?? 'rekord'}</Link>}
      </span>
      {a.error ? <ErrorBox error={a.error} /> : null}
    </li>
  );
}

/** Új feladat űrlapja – a tárgyhoz kötve, ha meg van adva. */
export function TaskForm({ subject, onDone, onCancel }: { subject?: { type: string; id: string }; onDone: () => void; onCancel?: () => void }) {
  const s = useSession();
  const members = useMembers();
  const [v, setV] = useState({ title: '', due_date: '', due_time: '', assignee_user_id: s.me!.user.id, priority: 'normal', description: '' });
  const a = useAsync();
  const set = (k: keyof typeof v) => (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value });
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const body = {
      title: v.title.trim(), due_date: v.due_date || null, due_time: v.due_date && v.due_time ? v.due_time : null,
      assignee_user_id: v.assignee_user_id || null, priority: v.priority, description: v.description.trim() || null,
      ...(subject ? { subject_type: subject.type, subject_id: subject.id } : {}),
    };
    if (await a.run(() => api('/tasks', { method: 'POST', body }))) { setV({ ...v, title: '', due_date: '', due_time: '', description: '' }); onDone(); }
  };
  return (
    <form className="form task-form" onSubmit={submit}>
      <input placeholder="Mi a teendő? (pl. Visszahívni az ajánlat miatt)" value={v.title} onChange={set('title')} required maxLength={300} />
      <div className="row">
        <input type="date" aria-label="Határidő" value={v.due_date} onChange={set('due_date')} />
        <input type="time" aria-label="Időpont" value={v.due_time} onChange={set('due_time')} disabled={!v.due_date} />
        <select aria-label="Felelős" value={v.assignee_user_id} onChange={set('assignee_user_id')}>
          {members.length === 0 && <option value={s.me!.user.id}>{s.me!.user.name}</option>}
          {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
        <select aria-label="Prioritás" value={v.priority} onChange={set('priority')}>
          <option value="normal">normál</option><option value="high">sürgős</option><option value="low">ráér</option>
        </select>
      </div>
      <ErrorBox error={a.error} />
      <div className="actions">
        <button type="submit" disabled={a.busy || !v.title.trim()}>Hozzáadás</button>
        {onCancel && <button type="button" className="secondary" onClick={onCancel}>Mégse</button>}
      </div>
    </form>
  );
}

/** Egy rekordhoz (cég / kontakt / deal) tartozó nyitott feladatok, új feladat hozzáadásával. */
export function TaskList({ subject, reloadKey = 0, onChange }: { subject: { type: 'company' | 'contact' | 'deal'; id: string }; reloadKey?: number; onChange?: () => void }) {
  const [items, setItems] = useState<Task[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const a = useAsync();
  const load = () => a.run(async () => setItems((await api<{ items: Task[] }>(
    `/tasks?subject_type=${subject.type}&subject_id=${subject.id}${showDone ? '' : '&status=open'}&limit=100`)).items));
  useEffect(() => { load(); }, [subject.type, subject.id, reloadKey, showDone]);
  const changed = () => { load(); onChange?.(); };
  return (
    <section className="card">
      <div className="card-head">
        <h3>Feladatok</h3>
        <label className="check muted small"><input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} /> lezártak is</label>
        {!adding && <button className="secondary" onClick={() => setAdding(true)}>Új feladat</button>}
      </div>
      {adding && <TaskForm subject={subject} onDone={() => { setAdding(false); changed(); }} onCancel={() => setAdding(false)} />}
      <ErrorBox error={a.error} />
      <ul className="plain tasks">
        {items?.map((t) => <TaskRow key={t.id} t={t} onChange={changed} showSubject={false} showAssignee />)}
        {items?.length === 0 && <li className="muted">Nincs {showDone ? '' : 'nyitott '}feladat.</li>}
      </ul>
    </section>
  );
}
